import { useStops } from '@ctcells/api'
import type { Batch } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { batchStatusLabel, t } from '@ctcells/i18n'
import { useNavigate } from 'react-router'

import { Button } from '../atoms/Button.tsx'
import { Icon } from '../atoms/Icon.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { StatusBadge } from '../atoms/StatusBadge.tsx'
import type { ActionGate, ScreenAccess } from '../screens/access.ts'

import { isGlobalEstop, systemSafetyStopOf } from './estopMode.ts'
import { StopsReleasePanel } from './StopsReleasePanel.tsx'
import './batchHalt.css'

export function BatchHaltDialog({
  batches,
  holder,
  holderName,
  access,
  onClose,
  onAcquireLock,
  acquireGate,
  acquireError,
}: {
  batches: readonly Batch[]
  holder: string
  /** 내가 쥐었을 때 보유자 표시 이름(`holder.display_name`). 없으면 「내가 보유」 */
  holderName?: string
  access: ScreenAccess
  onClose: () => void
  onAcquireLock?: () => void
  acquireGate?: ActionGate
  acquireError?: string | null
}) {
  const navigate = useNavigate()
  const stops = useStops()
  const systemSafetyStop = systemSafetyStopOf(stops.data)
  const go = (path: string) => {
    onClose()
    void navigate(path)
  }
  /*
   * E-STOP 모드 — 「전체 비상정지 · 전 장비」를 고르는 조건 그대로다(새 판정을 짓지 않는다).
   * eric 2026-10-29 「E-STOP 이 뜨면... 너무 평온한데… E-STOP 실행 중 타이틀 · 붉은색으로 깜빡이게」.
   * 그 밖의 멈춤(보상 · 격리 …)은 종전 모양 그대로다.
   */
  const estop = isGlobalEstop(systemSafetyStop, batches)
  return (
    <Modal
      title={t(estop ? 'batchHalt.estopTitle' : 'batchHalt.title')}
      {...(estop
        ? {
            titleMark: (
              <span aria-hidden="true" className="batch-halt__estop-mark">
                <Icon name="alert" />
              </span>
            ),
          }
        : {})}
      size="md"
      scope="body"
      onClose={onClose}
      /*
       * 아래 단추 줄이 없다 — [작업 상세] · [알람 보기] · [닫기]를 걷었다(eric 2026-10-29 「여기에.. 작업 상세 알람 보기 없애줘」 ·
       * 「닫기 버튼 없애고 대신 오른쪽 위에 X 크게 해줘」). 닫기는 머리의 ✕(`.a-modal__close` · 이 팝업에서만 큰 단 · `batchHalt.css`)와 Esc 다
       */
    >
      <div className={estop ? 'batch-halt batch-halt--estop' : 'batch-halt'}>
        {access.controlDisabled ? <p role="status">{t('batchHalt.stale')}</p> : null}
        <div className="batch-halt__list">
          {batches.map((batch) => {
            /*
             * 🔴 전체 비상정지(`estopMode` 와 같은 판정)로 멈춘 작업 — 작업 줄 오른쪽 배지가 붉은 「▲ E-STOP」(작업 줄 머리 배지와 같은 글 · 톤)이고
             *    사유 상자는 통째로 없다(eric 2026-10-29 「일시정지 오른쪽에 나오는데.. 그거 없애고.. 전체 비상정지 이거도 지우고.. 오른쪽 위에
             *    E-STOP 쓰고」). 머리 제목 「E-STOP 실행 중」이 같은 사실을 말한다. 그 밖의 멈춤(모듈 오류 · 일시정지 …)은 종전 배지 · 상자 그대로다
             */
            const globalStop = batch.halt_code === 'safety_stop' && systemSafetyStop
            return (
              <div key={batch.batch_id} className="batch-halt__item">
                <div className="batch-halt__heading">
                  <strong>{batch.name ?? batch.recipe_name ?? batch.batch_id}</strong>
                  <StatusBadge
                    status={batch.status ?? ''}
                    label={globalStop ? t('action.estop') : batchStatusLabel(batch.status ?? '')}
                    size="pill"
                    {...(globalStop ? { tone: 'danger' as const } : {})}
                  />
                  {batches.length > 1 ? (
                    <Button
                      size="pill"
                      onClick={() => go(`${PATHS.batches}/${encodeURIComponent(batch.batch_id)}`)}
                    >
                      {t('batchHalt.details')}
                    </Button>
                  ) : null}
                </div>
                {globalStop ? null : (
                  <section className="batch-halt__cause" aria-label={t('batchHalt.cause')}>
                    <div className="batch-halt__cause-heading">
                      {batch.halt_code ? (
                        <span aria-hidden="true" className="batch-halt__cause-mark">
                          ▲
                        </span>
                      ) : null}
                      <strong className="batch-halt__cause-title">
                        {batch.halt_module_id == null
                          ? t('batchHalt.cause')
                          : `${t('batchHalt.module')}: ${batch.halt_module_id}`}
                      </strong>
                      {batch.halt_module_id != null ? (
                        <Button
                          size="pill"
                          onClick={() =>
                            go(`${PATHS.modules}/${encodeURIComponent(batch.halt_module_id!)}`)
                          }
                        >
                          {t('batchHalt.moduleOpen')}
                        </Button>
                      ) : null}
                      {batch.halt_code ? <small className="muted">{batch.halt_code}</small> : null}
                    </div>
                    <p className="batch-halt__reason">
                      {batch.halt_reason?.trim()
                        ? batch.halt_reason
                        : t(batch.status === 'paused' ? 'batchHalt.paused' : 'batchHalt.unknown')}
                    </p>
                  </section>
                )}
              </div>
            )
          })}
        </div>
        <h3>{t('batchHalt.steps')}</h3>
        <ol className="batch-halt__steps" aria-label={t('batchHalt.steps')}>
          <li className="batch-halt__step">
            <span className="batch-halt__number" aria-hidden="true">
              1
            </span>
            <strong className="batch-halt__step-title">{t('lock.label')}</strong>
            <div className="batch-halt__heading">
              <span>
                {access.lockHeld ? <span aria-hidden="true">✓ </span> : null}
                {access.lockHeld
                  ? holderName === undefined || holderName.trim() === ''
                    ? t('batchHalt.lockMine')
                    : t('batchHalt.lockHolder', { holder: holderName })
                  : holder === t('lock.none') ||
                      holder === t('lock.unknown') ||
                      holder === t('lock.expired') ||
                      holder === t('lock.holderUnknown')
                    ? holder
                    : t('batchHalt.lockHolder', { holder })}
              </span>
              {!access.lockHeld && (holder === t('lock.none') || holder === t('lock.expired')) ? (
                <Button size="pill" {...acquireGate} onClick={onAcquireLock}>
                  {t('action.acquireLock')}
                </Button>
              ) : !access.lockHeld && holder !== t('lock.unknown') ? (
                <small className="muted">{t('batchHalt.lockOther')}</small>
              ) : null}
              {acquireError ? <span role="alert">{acquireError}</span> : null}
            </div>
          </li>
          <li className="batch-halt__step">
            <span className="batch-halt__number" aria-hidden="true">
              2
            </span>
            <strong className="batch-halt__step-title">{t('batchHalt.stops')}</strong>
            <StopsReleasePanel access={access} hideTitle />
          </li>
          {/*
           * ③ 「작업 계속 · [재개]」 단계는 걷었다 — 같은 [재개]가 작업 줄 머리에 있다(eric 2026-10-29 「작업 계속 재개.. 이거 밖에 있는 거
           * 아니야?」). 해소 순서는 제어락 → 정지 풀기 둘이고, 정지 풀기 결과 줄이 작업 줄의 [재개]를 가리킨다(`stopsRelease.after`)
           */}
        </ol>
      </div>
    </Modal>
  )
}
